"use client";

import { ChevronLeft, ChevronRight, Ellipsis } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import cn from "twsx";
import styles from "./List.module.css";

const horizontalOverflowFadeSize = 20;

type OverflowEdges = {
  start: boolean;
  end: boolean;
};

export function HorizontalOverflowLane({
  children,
  className,
  ariaLabel,
  showEdgeAffordances = false,
  edgeAffordance = "chevron",
}: {
  children: React.ReactNode;
  className?: string;
  ariaLabel: string;
  showEdgeAffordances?: boolean;
  edgeAffordance?: "chevron" | "ellipsis";
}) {
  const laneRef = useRef<HTMLDivElement | null>(null);
  const contentRef = useRef<HTMLDivElement | null>(null);
  const [edges, setEdges] = useState<OverflowEdges>({
    start: false,
    end: false,
  });

  const syncEdges = useCallback(() => {
    const lane = laneRef.current;
    if (!lane) return;

    const maxScroll = lane.scrollWidth - lane.clientWidth;
    setEdges({
      start: lane.scrollLeft > 1,
      end: lane.scrollLeft < maxScroll - 1,
    });
  }, []);

  useEffect(() => {
    const lane = laneRef.current;
    if (!lane) return;

    const revealFocusedControl = (event: FocusEvent) => {
      const target = event.target;
      if (!(target instanceof HTMLElement) || !lane.contains(target)) return;

      const laneRect = lane.getBoundingClientRect();
      const targetRect = target.getBoundingClientRect();

      if (targetRect.left < laneRect.left) {
        lane.scrollLeft -= laneRect.left - targetRect.left + horizontalOverflowFadeSize;
      } else if (targetRect.right > laneRect.right) {
        lane.scrollLeft += targetRect.right - laneRect.right + horizontalOverflowFadeSize;
      }
      syncEdges();
    };

    syncEdges();
    lane.addEventListener("scroll", syncEdges, { passive: true });
    lane.addEventListener("focusin", revealFocusedControl);
    const resizeObserver = new ResizeObserver(syncEdges);
    resizeObserver.observe(lane);
    if (contentRef.current) {
      resizeObserver.observe(contentRef.current);
    }

    return () => {
      lane.removeEventListener("scroll", syncEdges);
      lane.removeEventListener("focusin", revealFocusedControl);
      resizeObserver.disconnect();
    };
  }, [syncEdges]);

  const affordanceClassName = cn(
    "pointer-events-none absolute inset-y-0 z-10 w-7 items-center text-text-tertiary",
    edgeAffordance === "ellipsis" ? "flex" : "hidden max-md:flex",
  );

  return (
    <div className="relative min-w-0 w-full max-w-full">
      <div
        ref={laneRef}
        role="region"
        aria-label={ariaLabel}
        data-horizontal-overflow-lane="true"
        data-fade-start={edges.start || undefined}
        data-fade-end={edges.end || undefined}
        data-affordances={showEdgeAffordances || undefined}
        className={cn(styles.overflowLane, "no-scrollbar -m-1 min-w-0 overflow-x-auto overscroll-x-contain p-1", className)}
      >
        <div ref={contentRef} className="w-max min-w-full">
          {children}
        </div>
      </div>
      {showEdgeAffordances && edges.start && (
        <div
          aria-hidden="true"
          className={cn(affordanceClassName, "left-0 justify-start bg-gradient-to-r from-bg-surface via-bg-surface/90 to-transparent")}
          data-horizontal-overflow-affordance="start"
        >
          {edgeAffordance === "ellipsis" ? <Ellipsis className="size-4" /> : <ChevronLeft className="size-3.5" strokeWidth={1.75} />}
        </div>
      )}
      {showEdgeAffordances && edges.end && (
        <div
          aria-hidden="true"
          className={cn(affordanceClassName, "right-0 justify-end bg-gradient-to-l from-bg-surface via-bg-surface/90 to-transparent")}
          data-horizontal-overflow-affordance="end"
        >
          {edgeAffordance === "ellipsis" ? <Ellipsis className="size-4" /> : <ChevronRight className="size-3.5" strokeWidth={1.75} />}
        </div>
      )}
    </div>
  );
}
